import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

function toArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

function CompanyStatisticsWidget({ companyId }) {
    // Load all data
    const { data: jobsData, isLoading: jobsLoading } = useQuery(
        ['companyJobs', companyId],
        () => JobHttpService.loadJobs({ filters: { company: companyId }, size: 1000 }),
        { enabled: !!companyId }
    );

    const { data: employeesData, isLoading: employeesLoading } = useQuery(
        ['companyEmployees', companyId],
        () => JobHttpService.loadEmployees({ company: companyId }),
        { enabled: !!companyId }
    );

    const { data: salarySpecsData, isLoading: salarySpecsLoading } = useQuery(
        ['companySalarySpecs', companyId],
        () => JobHttpService.loadSalarySpecifications({ company: companyId }),
        { enabled: !!companyId }
    );

    const { data: invoicesData, isLoading: invoicesLoading } = useQuery(
        ['companyInvoices', companyId],
        () => JobHttpService.loadInvoices({ company: companyId }),
        { enabled: !!companyId }
    );

    const { data: contractsData, isLoading: contractsLoading } = useQuery(
        ['companyContracts', companyId],
        () => JobHttpService.loadContracts({ company: companyId }),
        { enabled: !!companyId }
    );

    const jobs = jobsData?.results || [];
    const jobIds = jobs.map(job => job.id);

    const { data: applicationsData, isLoading: applicationsLoading } = useQuery(
        ['companyApplications', jobIds.join(',')],
        async () => {
            if (!jobIds || jobIds.length === 0) return [];
            try {
                const response = await JobHttpService.loadJobApplications({ size: 1000 });
                const allApps = toArray(response);
                const filteredApps = allApps.filter(app => {
                    const appJobId = app.job?.id || app.job;
                    return jobIds.includes(appJobId);
                });
                return filteredApps;
            } catch (err) {
                console.error('Error loading applications:', err);
                return [];
            }
        },
        { enabled: !!companyId && jobIds.length > 0 }
    );

    const employees = toArray(employeesData);
    const invoices = toArray(invoicesData);
    const salarySpecs = toArray(salarySpecsData);
    const contracts = toArray(contractsData);
    const applications = toArray(applicationsData);

    const isLoading = jobsLoading || employeesLoading || salarySpecsLoading || 
                     invoicesLoading || contractsLoading || applicationsLoading;

    // Chart 1: Overview Statistics Bar Chart
    const overviewData = useMemo(() => [
        { name: 'Jobs', value: jobs.length },
        { name: 'Applications', value: applications.length },
        { name: 'Employees', value: employees.length },
        { name: 'Contracts', value: contracts.length },
        { name: 'Salary Specs', value: salarySpecs.length },
        { name: 'Invoices', value: invoices.length }
    ], [jobs.length, applications.length, employees.length, contracts.length, salarySpecs.length, invoices.length]);

    // Chart 2: Jobs Status Pie Chart
    const jobsStatusData = useMemo(() => {
        const statusCounts = {};
        jobs.forEach(job => {
            let status = job.status;
            // Normalize boolean values to readable strings
            if (status === true || status === 'true') {
                status = 'Active';
            } else if (status === false || status === 'false') {
                status = 'Inactive';
            } else {
                status = status || 'Active';
                // Capitalize first letter
                status = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            }
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        return Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
    }, [jobs]);

    // Chart 3: Applications Over Time Line Chart
    const applicationsOverTime = useMemo(() => {
        const monthlyData = {};
        applications.forEach(app => {
            const date = new Date(app.created_at || app.created_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
        });

        return Object.entries(monthlyData)
            .map(([month, count]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                count
            }))
            .sort((a, b) => new Date(a.month) - new Date(b.month))
            .slice(-12); // Last 12 months
    }, [applications]);

    // Chart 4: Jobs Created Over Time Area Chart
    const jobsOverTime = useMemo(() => {
        const monthlyData = {};
        jobs.forEach(job => {
            const date = new Date(job.created_at || job.created_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
        });

        return Object.entries(monthlyData)
            .map(([month, count]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                count
            }))
            .sort((a, b) => new Date(a.month) - new Date(b.month))
            .slice(-12);
    }, [jobs]);

    // Chart 5: Application Status Distribution Pie Chart
    const applicationStatusData = useMemo(() => {
        const statusCounts = {};
        applications.forEach(app => {
            const status = app.status || app.decision || 'pending';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        return Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
    }, [applications]);

    // Chart 6: Contracts vs Invoices Comparison Bar Chart
    const contractsInvoicesData = useMemo(() => {
        const monthlyData = {};
        
        contracts.forEach(contract => {
            const date = new Date(contract.created_at || contract.created_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = { month: new Date(monthKey + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), contracts: 0, invoices: 0 };
            }
            monthlyData[monthKey].contracts += 1;
        });

        invoices.forEach(invoice => {
            const date = new Date(invoice.created_at || invoice.created_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = { month: new Date(monthKey + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), contracts: 0, invoices: 0 };
            }
            monthlyData[monthKey].invoices += 1;
        });

        return Object.values(monthlyData)
            .sort((a, b) => new Date(a.month) - new Date(b.month))
            .slice(-6);
    }, [contracts, invoices]);

    // Table 1: Recent Jobs
    const recentJobs = useMemo(() => {
        return [...jobs]
            .sort((a, b) => new Date(b.created_at || b.created_date) - new Date(a.created_at || a.created_date))
            .slice(0, 5);
    }, [jobs]);

    // Table 2: Recent Applications
    const recentApplications = useMemo(() => {
        return [...applications]
            .sort((a, b) => new Date(b.created_at || b.created_date) - new Date(a.created_at || a.created_date))
            .slice(0, 5);
    }, [applications]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                Company Statistics
            </Typography>

            <Grid container spacing={3}>
                {/* Chart 1: Overview Statistics Bar Chart */}
                <Grid item xs={12} lg={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Overview Statistics
                            </Typography>
                            <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                                <ResponsiveContainer>
                                    <BarChart data={overviewData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="value" fill="#1976d2" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Chart 2: Jobs Status Pie Chart */}
                <Grid item xs={12} lg={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Jobs by Status
                            </Typography>
                            <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={jobsStatusData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {jobsStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Chart 3: Applications Over Time Line Chart */}
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Applications Received Over Time
                            </Typography>
                            <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                                <ResponsiveContainer>
                                    <LineChart data={applicationsOverTime}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="count" stroke="#00C49F" strokeWidth={2} name="Applications" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Chart 4: Jobs Created Over Time Area Chart */}
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Jobs Created Over Time
                            </Typography>
                            <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={jobsOverTime}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Area type="monotone" dataKey="count" stroke="#FF8042" fill="#FF8042" fillOpacity={0.6} name="Jobs" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Chart 5: Application Status Distribution Pie Chart */}
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Application Status Distribution
                            </Typography>
                            <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={applicationStatusData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, value }) => `${name}: ${value}`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {applicationStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Chart 6: Contracts vs Invoices Comparison */}
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Contracts vs Invoices (Last 6 Months)
                            </Typography>
                            <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                                <ResponsiveContainer>
                                    <BarChart data={contractsInvoicesData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="contracts" fill="#0088FE" name="Contracts" />
                                        <Bar dataKey="invoices" fill="#00C49F" name="Invoices" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Table 1: Recent Jobs */}
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Recent Jobs
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Title</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Applications</strong></TableCell>
                                            <TableCell><strong>Created</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recentJobs.length > 0 ? (
                                            recentJobs.map((job) => {
                                                const jobApplications = applications.filter(app => {
                                                    const appJobId = app.job?.id || app.job;
                                                    return appJobId === job.id;
                                                });
                                                return (
                                                    <TableRow key={job.id}>
                                                        <TableCell>{job.title || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={job.status || 'active'} 
                                                                size="small"
                                                                color={job.status === 'active' ? 'success' : 'default'}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{jobApplications.length}</TableCell>
                                                        <TableCell>
                                                            {job.created_at 
                                                                ? new Date(job.created_at).toLocaleDateString()
                                                                : 'N/A'}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">No jobs found</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Table 2: Recent Applications */}
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Recent Applications
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Candidate</strong></TableCell>
                                            <TableCell><strong>Job</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Date</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recentApplications.length > 0 ? (
                                            recentApplications.map((app) => (
                                                <TableRow key={app.id}>
                                                    <TableCell>
                                                        {app.profile?.first_name && app.profile?.last_name
                                                            ? `${app.profile.first_name} ${app.profile.last_name}`
                                                            : app.candidate_name || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {app.job?.title || app.job_title || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={app.status || app.decision || 'pending'} 
                                                            size="small"
                                                            color={
                                                                app.status === 'hired' || app.decision === 'hired' ? 'success' :
                                                                app.status === 'rejected' || app.decision === 'rejected' ? 'error' :
                                                                'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {app.created_at 
                                                            ? new Date(app.created_at).toLocaleDateString()
                                                            : 'N/A'}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">No applications found</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default CompanyStatisticsWidget;
